import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Settings2, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import {
  getParametrosEncargosMaoDeObra, putParametroEncargoMaoDeObra,
  getIncluirNoTotalDepartamentoMaoDeObra, putIncluirNoTotalDepartamentoMaoDeObra,
  getParametrosBeneficiosMaoDeObra, putParametroBeneficioMaoDeObra,
  getRoleFromToken,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { DEPARTAMENTOS_GESTAO_ORCAMENTARIA } from "../../constants/departamentosOrcamento";
import { MESES_ABREV } from "../../utils/orcamento";

const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);
const REGIMES_ENCARGO = ["CLT", "Estagio"];
const REGIMES_TABS = [
  { value: "CLT", label: "CLT" },
  { value: "Estagio", label: "Estágio" },
  { value: "PJ", label: "PJ" },
];

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
  borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
};

// Premissas · Mão de Obra: extraído de MaoDeObraView.js (era um painel colapsável dentro de
// Custo/Despesas Mão de Obra) — aqui é a tela dedicada, sempre carregada aberta. Percentuais/
// valores de Encargos/Benefícios são globais por regime (CLT/Estágio/PJ); "Incluir no Total" é
// por DEPARTAMENTO (alguns aceitam um encargo no custo total, ex. FGTS Multa, outros não), por
// isso a página precisa de um seletor de departamento — igual ao já usado em Custo/Despesas
// Mão de Obra, mas aqui com a lista completa (não fixada em Operação x demais).
export default function PremissasMaoDeObraView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const isAdmin = getRoleFromToken() === "admin";

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [departamento, setDepartamento] = useState(DEPARTAMENTOS_GESTAO_ORCAMENTARIA[0]);
  const [erro, setErro] = useState("");

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria
        isLight={isLight}
        departamentos={DEPARTAMENTOS_GESTAO_ORCAMENTARIA}
        departamentoAtual={departamento}
        onSelecionarDepartamento={setDepartamento}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Premissas · Mão de Obra</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", fontSize: 13, fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={departamento}>
              {departamento}
            </span>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}>
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={headerBtnStyle}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={headerBtnStyle}>Menu Principal</button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`, border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13 }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: C.text }}>Premissas · Mão de Obra</h1>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: C.muted }}>
              Percentuais de Encargos e valores de Benefícios usados no cálculo de custo de todo colaborador (Custo/Despesas Mão de Obra) — globais por regime, exceto "Incluir no Total", que é por departamento.
            </p>
          </div>

          <PainelParametros C={C} ano={ano} departamento={departamento} onErro={setErro} isAdmin={isAdmin} />
        </main>
      </div>
    </div>
  );
}

function PainelParametros({ C, ano, departamento, onErro, isAdmin }) {
  const [aba, setAba] = useState("CLT");
  const [encargos, setEncargos] = useState([]);
  const [beneficios, setBeneficios] = useState([]);
  const [mapaIncluirNoTotal, setMapaIncluirNoTotal] = useState({}); // "regime:codigo" → bool, já resolvido para `departamento`
  const [carregando, setCarregando] = useState(false);

  async function carregar() {
    setCarregando(true);
    try {
      await recarregarSilencioso();
    } catch (e) {
      onErro(e.message);
    } finally {
      setCarregando(false);
    }
  }

  // Recarrega os dados sem exibir o indicador de carregamento — usada após editar uma
  // célula, para não desmontar os itens (o que fecharia as subcategorias já abertas).
  async function recarregarSilencioso() {
    const [e, b, incluirNoTotal] = await Promise.all([
      getParametrosEncargosMaoDeObra(ano), getParametrosBeneficiosMaoDeObra(ano),
      getIncluirNoTotalDepartamentoMaoDeObra(ano, departamento),
    ]);
    setEncargos(e || []);
    setBeneficios(b || []);
    setMapaIncluirNoTotal(incluirNoTotal || {});
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, [ano, departamento]);

  async function salvarCelulaEncargo(item, mes, percentual) {
    await putParametroEncargoMaoDeObra(item.ids_mensais[mes], { percentual: percentual / 100 });
    await recarregarSilencioso();
  }

  async function aplicarEncargo(item, meses, percentual) {
    await Promise.all(meses.map((mes) => putParametroEncargoMaoDeObra(item.ids_mensais[mes], { percentual: percentual / 100 })));
    await recarregarSilencioso();
  }

  // "Incluir no Total" é por departamento (não por item global) — só altera o override do
  // departamento em exibição no momento, sem afetar os demais (ver constants/departamentosOrcamento).
  async function alternarIncluirNoTotalEncargo(item, incluirNoTotal) {
    await putIncluirNoTotalDepartamentoMaoDeObra(item.regime, item.codigo, ano, departamento, incluirNoTotal);
    await recarregarSilencioso();
  }

  async function salvarCelulaBeneficio(item, mes, valor) {
    await putParametroBeneficioMaoDeObra(item.ids_mensais[mes], { valor });
    await recarregarSilencioso();
  }

  async function aplicarBeneficio(item, meses, valor) {
    await Promise.all(meses.map((mes) => putParametroBeneficioMaoDeObra(item.ids_mensais[mes], { valor })));
    await recarregarSilencioso();
  }

  const encargosDaAba = encargos.filter((e) => e.regime === aba);
  const beneficiosDaAba = beneficios.filter((b) => b.regime === aba);

  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Settings2 size={15} color={C.brand} />
          <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: C.text }}>Parâmetros de Encargos e Benefícios</h2>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: C.brand, padding: "2px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {isAdmin ? "Editável pelo financeiro" : "Somente visualização"}
          </span>
        </div>
      </div>

      <div style={{ padding: "0 18px 18px" }}>
        <p style={{ margin: "0 0 12px", fontSize: 11, color: C.muted }}>
          Nada aqui é fixo — os valores podem mudar mês a mês (reajuste de plano de saúde, mudança de alíquota, etc.).
        </p>
        <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
          {REGIMES_TABS.map((r) => (
            <button key={r.value} onClick={() => setAba(r.value)} style={{
              padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
              background: aba === r.value ? C.brand : C.bg, color: aba === r.value ? "#fff" : C.text,
              fontSize: 12, fontWeight: 700, fontFamily: "inherit",
            }}>
              {r.label}
            </button>
          ))}
        </div>

        {carregando ? (
          <p style={{ fontSize: 12, color: C.muted }}>Carregando parâmetros…</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Encargos (% sobre o salário do mês)</p>
              {!REGIMES_ENCARGO.includes(aba) ? (
                <p style={{ fontSize: 12, color: C.muted }}>PJ não possui encargos.</p>
              ) : encargosDaAba.length === 0 ? (
                <p style={{ fontSize: 12, color: C.muted }}>Nenhum parâmetro cadastrado.</p>
              ) : encargosDaAba.map((item) => (
                <ItemParametroMensal key={`${item.regime}-${item.codigo}`} C={C} sufixo="%" fator={100}
                  item={{ ...item, incluir_no_total: mapaIncluirNoTotal[`${item.regime}:${item.codigo}`] !== false }}
                  onSalvarCelula={salvarCelulaEncargo} onAplicar={aplicarEncargo}
                  onToggleIncluirNoTotal={alternarIncluirNoTotalEncargo} isAdmin={isAdmin} />
              ))}
            </div>
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Benefícios (R$/mês)</p>
              {beneficiosDaAba.length === 0 ? (
                <p style={{ fontSize: 12, color: C.muted }}>Nenhum parâmetro cadastrado.</p>
              ) : beneficiosDaAba.map((item) => (
                <ItemParametroMensal key={`${item.regime}-${item.codigo}`} C={C}
                  item={{ ...item, label: `${item.label}${item.apenas_lider ? " (Líder)" : item.apenas_colaborador ? " (Colaborador)" : ""}${item.exclui_remoto ? " (não aplica a Remoto)" : ""}` }}
                  prefixo="R$" fator={1} onSalvarCelula={salvarCelulaBeneficio} onAplicar={aplicarBeneficio} isAdmin={isAdmin} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function formatarValor(valor, prefixo, sufixo) {
  if (prefixo) return `${prefixo} ${valor}`;
  return `${valor}${sufixo || ""}`;
}

// Item com 12 células mensais (Jan–Dez) + ações "Aplicar a todos os meses"/"Aplicar deste
// mês em diante", que tomam como base o último valor editado nesse item.
function ItemParametroMensal({ C, item, prefixo, sufixo, fator, onSalvarCelula, onAplicar, onToggleIncluirNoTotal, isAdmin }) {
  const [aberto, setAberto] = useState(false);
  const [ultimaEdicao, setUltimaEdicao] = useState(null); // { mes, valor }

  function registrarEdicao(mes, valor) {
    if (!isAdmin) return;
    setUltimaEdicao({ mes, valor });
    onSalvarCelula(item, mes, valor);
  }

  function aplicarTodos() {
    if (!ultimaEdicao) return;
    const meses = Array.from({ length: 12 }, (_, i) => i + 1);
    if (!window.confirm(`Aplicar "${formatarValor(ultimaEdicao.valor, prefixo, sufixo)}" (${MESES_ABREV[ultimaEdicao.mes - 1]}) para todos os 12 meses de "${item.label}"? Isso sobrescreve os outros meses.`)) return;
    onAplicar(item, meses, ultimaEdicao.valor);
  }

  function aplicarDesteMesEmDiante() {
    if (!ultimaEdicao) return;
    const meses = Array.from({ length: 12 }, (_, i) => i + 1).filter((m) => m >= ultimaEdicao.mes);
    if (!window.confirm(`Aplicar "${formatarValor(ultimaEdicao.valor, prefixo, sufixo)}" de ${MESES_ABREV[ultimaEdicao.mes - 1]} em diante para "${item.label}"?`)) return;
    onAplicar(item, meses, ultimaEdicao.valor);
  }

  const resumo = resumoItem(item, prefixo, sufixo, fator);

  return (
    <div style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => setAberto((v) => !v)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, cursor: "pointer", padding: "2px 0" }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, minWidth: 0 }}>
          {aberto ? <ChevronDown size={13} color={C.muted} style={{ flexShrink: 0 }} /> : <ChevronRight size={13} color={C.muted} style={{ flexShrink: 0 }} />}
          <span style={{ color: C.text, fontWeight: 600 }}>{item.label}</span>
          <span style={{ color: C.muted }}>— {resumo}</span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }} onClick={(e) => e.stopPropagation()}>
          {onToggleIncluirNoTotal && (
            <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: C.muted, cursor: isAdmin ? "pointer" : "default" }}
              title="Se desmarcado, o item continua calculado e aparece na composição do custo, mas não soma no Total de Encargos/custo total">
              <input type="checkbox" checked={!!item.incluir_no_total} disabled={!isAdmin}
                onChange={(e) => onToggleIncluirNoTotal(item, e.target.checked)}
                style={{ cursor: isAdmin ? "pointer" : "default" }} />
              Incluir no Total
            </label>
          )}
          {aberto && isAdmin && (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={aplicarTodos} disabled={!ultimaEdicao} style={miniBtnStyle(C, !ultimaEdicao)}>Aplicar a todos os meses</button>
              <button onClick={aplicarDesteMesEmDiante} disabled={!ultimaEdicao} style={miniBtnStyle(C, !ultimaEdicao)}>Aplicar deste mês em diante</button>
            </div>
          )}
        </div>
      </div>
      {aberto && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(12, minmax(52px, 1fr))", gap: 4, overflowX: "auto", marginTop: 8 }}>
          {MESES_ABREV.map((m, i) => {
            const mes = i + 1;
            return (
              <CelulaParametroMes key={mes} C={C} mesLabel={m} valor={Number(item.valores_mensais[mes] || 0) * fator}
                prefixo={prefixo} sufixo={sufixo} onSalvar={(v) => registrarEdicao(mes, v)} disabled={!isAdmin} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// Resumo do item fechado — mesmo valor nos 12 meses, uma única mudança no ano (com o mês em
// que ela começa), ou "varia ao longo do ano" quando há mais de uma mudança.
function resumoItem(item, prefixo, sufixo, fator) {
  const arredondar = (v) => Math.round(v * 10000) / 10000;
  const valores = Array.from({ length: 12 }, (_, i) => arredondar(Number(item.valores_mensais[i + 1] || 0) * fator));

  if (valores.every((v) => v === valores[0])) {
    return `${formatarValor(valores[0], prefixo, sufixo)} (todos os meses)`;
  }

  const mudancas = [];
  for (let i = 1; i < 12; i++) {
    if (valores[i] !== valores[i - 1]) mudancas.push(i);
  }
  if (mudancas.length === 1) {
    const idx = mudancas[0];
    return `varia (${formatarValor(valores[idx - 1], prefixo, sufixo)} → ${formatarValor(valores[idx], prefixo, sufixo)} a partir de ${MESES_ABREV[idx]})`;
  }
  return "varia ao longo do ano";
}

function CelulaParametroMes({ C, mesLabel, valor, prefixo, sufixo, onSalvar, disabled }) {
  const [texto, setTexto] = useState(String(valor));
  const [editando, setEditando] = useState(false);

  useEffect(() => { if (!editando) setTexto(String(valor)); }, [valor, editando]);

  function confirmar() {
    setEditando(false);
    const novo = parseFloat(String(texto).replace(",", ".")) || 0;
    if (novo !== Number(valor)) onSalvar(novo);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
      <span style={{ fontSize: 9, color: C.muted, fontWeight: 600 }}>{mesLabel}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
        {prefixo && <span style={{ fontSize: 9, color: C.muted }}>{prefixo}</span>}
        <input type="text" inputMode="decimal" value={editando ? texto : String(valor)} disabled={disabled}
          onFocus={() => { setEditando(true); setTexto(String(valor)); }}
          onChange={(e) => setTexto(e.target.value)} onBlur={confirmar}
          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
          style={{ width: 48, textAlign: "right", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 4px", fontSize: 11, color: C.text, fontFamily: "inherit", outline: "none", opacity: disabled ? 0.6 : 1 }} />
        {sufixo && <span style={{ fontSize: 9, color: C.muted }}>{sufixo}</span>}
      </span>
    </div>
  );
}

function miniBtnStyle(C, disabled) {
  return {
    background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
    padding: "4px 8px", fontSize: 10, fontWeight: 600, color: disabled ? C.muted : C.brand,
    cursor: disabled ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: disabled ? 0.5 : 1,
  };
}
